import styled from 'styled-components';
import { Colors, FontSize, Spacing, LineHeight } from '../../assets/GlobalStyle/Variables';

export const ManagementContainer = styled.div`
  padding: 1.5rem 2rem;
  background: linear-gradient(to bottom, #f8f5fa 0%, #ffffff 100%);
  min-height: calc(100vh - 68px);
`;

export const StyledPageTitle = styled.div`
  background: linear-gradient(135deg, ${Colors.primary} 0%, ${Colors.secondary} 100%);
  border-radius: 12px;
  padding: 1.5rem 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;

  h1 {
    color: ${Colors.white};
    font-size: ${FontSize.title};
    font-weight: 600;
    margin: 0;
    letter-spacing: -0.3px;
  }
`;

export const ContentCard = styled.div`
  background: ${Colors.white};
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  margin-top: 1.5rem;
`;

export const ContentCardTitle = styled.h2`
  margin: 0px 0px 1rem;
  font-size: ${FontSize.normal};
  font-weight: 600;
`;

export const SelectWrapper = styled.div`
  max-width: 320px;
  margin-bottom: 1.5rem;
`;

export const StyledSelect = styled.select`
  width: 100%;
  padding: 10px 14px;
  border: 2px solid ${Colors.lightGrey};
  border-radius: 8px;
  font-size: 14px;
  color: ${Colors.mainText};
  background: ${Colors.white};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Colors.secondary};
  }

  &:focus {
    outline: none;
    border-color: ${Colors.primary};
    box-shadow: 0 0 0 3px rgba(60, 17, 73, 0.1);
  }
`;

export const TableCard = styled.div`
  background: ${Colors.white};
  overflow: hidden;
`;

export const StyledTable = styled.table`
  width: 100%;
  font-size: 14px;
  border-collapse: collapse;
`;

export const StyledTableHead = styled.thead`
  background-color: ${Colors.lightBackground};

  th {
    padding: ${Spacing(4)} ${Spacing(4)} ${Spacing(3)};
    text-align: left;
    font-weight: 600;
    color: ${Colors.mainText};
    border-bottom: 2px solid ${Colors.lightGrey};
    white-space: nowrap;
  }
`;

export const StyledTableBody = styled.tbody`
  tr {
    border-bottom: 1px solid ${Colors.lightGrey};
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #faf5fc;
    }

    &:last-child {
      border-bottom: none;
    }
    
    &.inactive {
      background-color: ${Colors.disabled};
    }
  }

  td {
    padding: ${Spacing(3)} ${Spacing(4)};
    color: ${Colors.mainText};
    line-height: ${LineHeight.double};
  }
`;

export const ActionCell = styled.td`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: center;
`;

export const IconButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  all: initial;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s ease;

  ${({ variant }) =>
    variant === 'delete'
      ? `
    color: ${Colors.error};
    &:hover {
      background-color: #fde8ea;
    }
  `
      : `
    color: ${Colors.secondary};
    &:hover {
      background-color: ${Colors.lightBackground};
    }
  `}
`;

export const PaginationWrapper = styled.div`
  margin-top: 2rem;
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${Colors.grey};
  font-size: 15px;
`;

export const Badge = styled.span<{ active: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  line-height: ${LineHeight.normal};

  ${({ active }) =>
    active
      ? `
    background-color: ${Colors.success};
    color: #0a5c36;
  `
      : `
    background-color: #f8d7da;
    color: #842029;
  `}
`;

// Modal styles
export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const ModalContent = styled.div`
  background: ${Colors.white};
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 560px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  max-height: 90vh;
  overflow-y: auto;
`;

export const ModalTitle = styled.h2`
  margin: 0 0 1.5rem 0;
  font-size: ${FontSize.subTitle};
  color: ${Colors.primary};
`;

export const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

export const FormLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  color: ${Colors.mainText};
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 2px solid ${Colors.lightGrey};
  border-radius: 8px;
  font-size: 14px;
  color: ${Colors.mainText};
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${Colors.primary};
    box-shadow: 0 0 0 3px rgba(60, 17, 73, 0.1);
  }
`;

export const FormCheckbox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;

  input[type='checkbox'] {
    width: 18px;
    height: 18px;
    accent-color: ${Colors.primary};
    cursor: pointer;
  }

  label {
    font-size: 14px;
    color: ${Colors.mainText};
    margin: 0;
    cursor: pointer;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 1.5rem;
`;

export const PlaceholderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: ${Colors.grey};
  text-align: center;

  h3 {
    margin: 1rem 0 0.5rem;
    font-size: ${FontSize.subTitle};
    color: ${Colors.mainText};
  }

  p {
    margin: 0;
    font-size: 14px;
  }
`;

export const StyledFilterForm = styled.form`
  margin: 0 ${Spacing(8)} ${Spacing(8)};
`;

export const StyledFilterCheckboxGroup = styled.div`
  display: flex;
  gap: ${Spacing(6)};
  align-items: center;
`;

export const StyledFilterButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${Spacing(4)};
`;